import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProducts } from './useProducts';
import { Helmet } from 'react-helmet-async';
import {
  mergeTaxonomyFromApi,
  normalizeCategoryList,
  normalizeCategorySlug,
  normalizeProductSlugKeys,
  resolveCategoryMeta,
} from './catalogNormalize';
import {
  sanitizeSlug,
  uniqueSlugFromName,
  defaultProduct,
  generateMeaningfulProductId,
} from './admin/adminUtils';
import { useAuth } from './auth/AuthContext';
import AdminNav from './admin/AdminNav';
import AdminOverviewPanel from './admin/AdminOverviewPanel';
import AdminAnalyticsPanel from './admin/AdminAnalyticsPanel';
import AdminCatalogTop from './admin/AdminCatalogTop';
import AdminShellFooter from './admin/AdminShellFooter';
import AdminTaxonomyPanel from './admin/AdminTaxonomyPanel';
import AdminProductsPanel from './admin/AdminProductsPanel';
import AdminJewelryProfilesPanel from './admin/AdminJewelryProfilesPanel';
import AdminAdvancedPanel from './admin/AdminAdvancedPanel';
import AdminOrdersPanel from './admin/AdminOrdersPanel';
import AdminBespokeRequestsPanel from './admin/AdminBespokeRequestsPanel';
import AdminCouponsPanel from './admin/AdminCouponsPanel';
import AdminReviewsPanel from './admin/AdminReviewsPanel';
import AdminShowsPanel from './admin/AdminShowsPanel';
import AdminSitePanel from './admin/AdminSitePanel';
import ProductEditorModal from './admin/ProductEditorModal';
import { CATALOG_SAVE_DEBOUNCE_MS, useCatalogPersist } from './admin/useCatalogPersist';
import './admin/admin-controls.css';
import './admin/admin-panel-ui.css';

const Admin = () => {
  const {
    products,
    categories: catalogCategories,
    loading,
    error,
    catalogDbActive,
    dataSource,
    refetch,
  } = useProducts();
  const [editableProducts, setEditableProducts] = useState({});
  const [editableCategories, setEditableCategories] = useState([]);
  const [status, setStatus] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [newCatHeroTagline, setNewCatHeroTagline] = useState('');
  const [newCatHeroBlurb, setNewCatHeroBlurb] = useState('');
  const [newSubName, setNewSubName] = useState('');
  const [newSubParent, setNewSubParent] = useState('');
  const [newTypeSlug, setNewTypeSlug] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeParent, setNewTypeParent] = useState('');
  const { token: sessionToken, username: adminUsername, role: userRole, ready: authReady, logout: authLogout } = useAuth();
  const navigate = useNavigate();
  const [adminTab, setAdminTab] = useState('overview');
  const [productSearch, setProductSearch] = useState('');
  const [productStockFilter, setProductStockFilter] = useState('');
  const [productModal, setProductModal] = useState({
    open: false,
    mode: 'add',
    category: '',
    index: null,
  });

  useEffect(() => {
    if (!authReady) return;
    const t = String(sessionToken || '').trim();
    if (!t) {
      navigate(`/login?next=${encodeURIComponent('/admin')}`, { replace: true });
      return;
    }
    if (userRole !== 'admin') {
      navigate('/account?from=admin', { replace: true });
    }
  }, [authReady, sessionToken, userRole, navigate]);

  const adminHeadersWith = useCallback((t) => {
    const tok = String(t || '').trim();
    return tok ? { 'X-Admin-Session': tok, Authorization: `Bearer ${tok}` } : {};
  }, []);

  const adminHeaders = useCallback(() => adminHeadersWith(sessionToken), [sessionToken, adminHeadersWith]);

  const { dirty, saving, queueCatalogSave, publishCatalog, clearDirty } = useCatalogPersist({
    sessionToken,
    catalogDbActive,
    editableCategories,
    editableProducts,
    refetch,
    navigate,
    getHeaders: adminHeaders,
    loading,
    setStatus,
  });

  useEffect(() => {
    if (loading || !products) return;
    if (productModal.open || dirty || saving) return;

    const mergedProducts = normalizeProductSlugKeys(products);
    const base = mergeTaxonomyFromApi(catalogCategories, mergedProducts);
    const meta = normalizeCategoryList(base, mergedProducts);
    const nextProducts = {};
    meta.forEach((c) => {
      nextProducts[c.slug] = Array.isArray(mergedProducts[c.slug])
        ? mergedProducts[c.slug].map((p) => ({ ...defaultProduct, ...p }))
        : [];
    });

    setEditableCategories(meta);
    setEditableProducts(nextProducts);
    clearDirty();
    setSelectedCategory((prev) => {
      if (prev && meta.some((m) => m.slug === prev)) return prev;
      return meta[0]?.slug || '';
    });
    setNewSubParent((p) => (p && meta.some((m) => m.slug === p) ? p : meta[0]?.slug || ''));
    setNewTypeParent((p) => (p && meta.some((m) => m.slug === p) ? p : meta[0]?.slug || ''));
  }, [loading, products, catalogCategories, clearDirty, dirty, saving, productModal.open]);

  const sortedEditableCategories = useMemo(
    () => [...editableCategories].sort((a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug)),
    [editableCategories]
  );

  const rawJson = useMemo(
    () => JSON.stringify({ categories: editableCategories, products: editableProducts }, null, 2),
    [editableCategories, editableProducts]
  );

  const updateCategoryName = (slug, name) => {
    setEditableCategories((prev) => prev.map((c) => (c.slug === slug ? { ...c, name } : c)));
    queueCatalogSave(CATALOG_SAVE_DEBOUNCE_MS);
  };

  const updateCategoryHero = (slug, field, value) => {
    if (field !== 'hero_tagline' && field !== 'hero_blurb') return;
    setEditableCategories((prev) =>
      prev.map((c) => (c.slug === slug ? { ...c, [field]: value } : c))
    );
    queueCatalogSave(CATALOG_SAVE_DEBOUNCE_MS);
  };

  const reorderCategories = (fromSlug, toSlug) => {
    if (!fromSlug || !toSlug || fromSlug === toSlug) return;
    setEditableCategories((prev) => {
      const sorted = [...prev].sort((a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug));
      const fromIdx = sorted.findIndex((c) => c.slug === fromSlug);
      const toIdx = sorted.findIndex((c) => c.slug === toSlug);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const copy = [...sorted];
      const [item] = copy.splice(fromIdx, 1);
      copy.splice(toIdx, 0, item);
      const categories = copy.map((c, i) => ({ ...c, sort_order: i }));
      queueCatalogSave(0, { categories, products: editableProducts });
      return categories;
    });
  };

  const addCategory = () => {
    const name = newCatName.trim();
    if (!name) {
      setStatus('Enter a category name.');
      return;
    }
    const slug = uniqueSlugFromName(name, editableCategories.map((c) => c.slug));
    if (!slug) {
      setStatus('Could not create a URL from that name. Try letters or numbers.');
      return;
    }
    const hero_tagline = String(newCatHeroTagline || '').trim();
    const hero_blurb = String(newCatHeroBlurb || '').trim();
    const categories = [
      ...editableCategories,
      {
        slug,
        name,
        sort_order: editableCategories.length,
        hero_tagline,
        hero_blurb,
        is_visible: true,
        subcategories: [],
        types: [],
        return_allowance: 'inherit',
      },
    ];
    const products = { ...editableProducts, [slug]: [] };
    setEditableCategories(categories);
    setEditableProducts(products);
    setSelectedCategory(slug);
    setNewCatName('');
    setNewCatHeroTagline('');
    setNewCatHeroBlurb('');
    queueCatalogSave(0, { categories, products });
    setStatus(`Added category “${name}”. Saving to shop…`);
  };

  const addSubcategory = () => {
    const parent = newSubParent || selectedCategory;
    if (!parent) {
      setStatus('Select a category first.');
      return;
    }
    const label = newSubName.trim();
    if (!label) {
      setStatus('Enter a group name.');
      return;
    }
    const cat = editableCategories.find((c) => c.slug === parent);
    const subs = [...(cat?.subcategories || [])];
    const subSlug = uniqueSlugFromName(label, subs.map((s) => s.slug));
    if (!subSlug) {
      setStatus('Could not create a URL from that name.');
      return;
    }
    const categories = editableCategories.map((c) =>
      c.slug === parent
        ? {
            ...c,
            subcategories: [
              ...subs,
              {
                slug: subSlug,
                name: label,
                sort_order: subs.length,
                is_visible: true,
                return_allowance: 'inherit',
              },
            ],
          }
        : c
    );
    setEditableCategories(categories);
    setNewSubName('');
    queueCatalogSave(0, { categories, products: editableProducts });
    setStatus(`Added subcategory “${label}”. Saving…`);
  };

  const addProductType = () => {
    const parent = newTypeParent || selectedCategory;
    if (!parent) {
      setStatus('Select a category first.');
      return;
    }
    const slug = sanitizeSlug(newTypeSlug);
    if (!slug) {
      setStatus('Enter a product type key (lowercase, hyphens).');
      return;
    }
    const cat = editableCategories.find((c) => c.slug === parent);
    const types = [...(cat?.types || [])];
    if (types.some((t) => t.slug === slug)) {
      setStatus('That product type key already exists in this category.');
      return;
    }
    const label = newTypeName.trim() || slug.charAt(0).toUpperCase() + slug.slice(1);
    const categories = editableCategories.map((c) =>
      c.slug === parent
        ? {
            ...c,
            types: [
              ...types,
              { slug, name: label, sort_order: types.length, is_visible: true, return_allowance: 'inherit' },
            ],
          }
        : c
    );
    setEditableCategories(categories);
    setNewTypeSlug('');
    setNewTypeName('');
    queueCatalogSave(0, { categories, products: editableProducts });
    setStatus(`Added product type “${label}”. Saving…`);
  };

  const deleteSubcategory = (categorySlug, subSlug) => {
    if (
      !window.confirm(
        `Remove subcategory “${subSlug}” from ${categorySlug}? Products in this group will move to “all”.`
      )
    ) {
      return;
    }
    const subKey = String(subSlug || '').trim().toLowerCase();
    const categories = editableCategories.map((c) => {
      if (c.slug !== categorySlug) return c;
      const nextSubs = (c.subcategories || [])
        .filter((s) => s.slug !== subSlug)
        .map((s, i) => ({ ...s, sort_order: i }));
      return { ...c, subcategories: nextSubs };
    });
    const products = { ...editableProducts };
    const list = [...(products[categorySlug] || [])];
    products[categorySlug] = list.map((p) => {
      const ps = String(p.subcategory || '').trim().toLowerCase();
      return ps === subKey ? { ...p, subcategory: 'all' } : p;
    });
    setEditableCategories(categories);
    setEditableProducts(products);
    queueCatalogSave(0, { categories, products });
    setStatus('Subcategory removed. Saving…');
  };

  const deleteProductType = (categorySlug, typeSlug) => {
    if (!window.confirm(`Remove product type “${typeSlug}” from ${categorySlug}?`)) return;
    const categories = editableCategories.map((c) => {
      if (c.slug !== categorySlug) return c;
      const nextTypes = (c.types || [])
        .filter((t) => t.slug !== typeSlug)
        .map((t, i) => ({ ...t, sort_order: i }));
      return { ...c, types: nextTypes };
    });
    const products = { ...editableProducts };
    const typeKey = String(typeSlug || '').trim().toLowerCase();
    const list = [...(products[categorySlug] || [])];
    products[categorySlug] = list.map((p) => {
      const pt = String(p.type || '').trim().toLowerCase();
      return pt === typeKey ? { ...p, type: '' } : p;
    });
    setEditableCategories(categories);
    setEditableProducts(products);
    queueCatalogSave(0, { categories, products });
    setStatus('Product type removed. Saving…');
  };

  const updateCategoryVisible = (slug, isVisible) => {
    const categories = editableCategories.map((c) =>
      c.slug === slug ? { ...c, is_visible: !!isVisible } : c
    );
    setEditableCategories(categories);
    queueCatalogSave(0, { categories, products: editableProducts });
  };

  const updateSubcategoryName = (categorySlug, subSlug, name) => {
    setEditableCategories((prev) =>
      prev.map((c) => {
        if (c.slug !== categorySlug) return c;
        const nextSubs = (c.subcategories || []).map((s) =>
          s.slug === subSlug ? { ...s, name } : s
        );
        return { ...c, subcategories: nextSubs };
      })
    );
    queueCatalogSave(CATALOG_SAVE_DEBOUNCE_MS);
  };

  const updateCategoryReturnAllowance = (categorySlug, value) => {
    const categories = editableCategories.map((c) =>
      c.slug === categorySlug ? { ...c, return_allowance: value } : c
    );
    setEditableCategories(categories);
    queueCatalogSave(0, { categories, products: editableProducts });
  };

  const updateSubcategoryReturnAllowance = (categorySlug, subSlug, value) => {
    const categories = editableCategories.map((c) => {
      if (c.slug !== categorySlug) return c;
      const nextSubs = (c.subcategories || []).map((s) =>
        s.slug === subSlug ? { ...s, return_allowance: value } : s
      );
      return { ...c, subcategories: nextSubs };
    });
    setEditableCategories(categories);
    queueCatalogSave(0, { categories, products: editableProducts });
  };

  const updateTypeReturnAllowance = (categorySlug, typeSlug, value) => {
    const categories = editableCategories.map((c) => {
      if (c.slug !== categorySlug) return c;
      const nextTypes = (c.types || []).map((t) =>
        t.slug === typeSlug ? { ...t, return_allowance: value } : t
      );
      return { ...c, types: nextTypes };
    });
    setEditableCategories(categories);
    queueCatalogSave(0, { categories, products: editableProducts });
  };

  const updateSubcategoryVisible = (categorySlug, subSlug, isVisible) => {
    const categories = editableCategories.map((c) => {
      if (c.slug !== categorySlug) return c;
      const nextSubs = (c.subcategories || []).map((s) =>
        s.slug === subSlug ? { ...s, is_visible: !!isVisible } : s
      );
      return { ...c, subcategories: nextSubs };
    });
    setEditableCategories(categories);
    queueCatalogSave(0, { categories, products: editableProducts });
  };

  const updateTypeVisible = (categorySlug, typeSlug, isVisible) => {
    const categories = editableCategories.map((c) => {
      if (c.slug !== categorySlug) return c;
      const nextTypes = (c.types || []).map((t) =>
        t.slug === typeSlug ? { ...t, is_visible: !!isVisible } : t
      );
      return { ...c, types: nextTypes };
    });
    setEditableCategories(categories);
    queueCatalogSave(0, { categories, products: editableProducts });
  };

  const reorderSubcategories = (categorySlug, fromSlug, toSlug) => {
    if (!fromSlug || !toSlug || fromSlug === toSlug) return;
    const categories = editableCategories.map((c) => {
      if (c.slug !== categorySlug) return c;
      const sorted = [...(c.subcategories || [])].sort(
        (a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug)
      );
      const fromIdx = sorted.findIndex((s) => s.slug === fromSlug);
      const toIdx = sorted.findIndex((s) => s.slug === toSlug);
      if (fromIdx < 0 || toIdx < 0) return c;
      const copy = [...sorted];
      const [item] = copy.splice(fromIdx, 1);
      copy.splice(toIdx, 0, item);
      return { ...c, subcategories: copy.map((s, i) => ({ ...s, sort_order: i })) };
    });
    setEditableCategories(categories);
    queueCatalogSave(0, { categories, products: editableProducts });
  };

  const moveProductType = (categorySlug, typeSlug, direction) => {
    const categories = editableCategories.map((c) => {
      if (c.slug !== categorySlug) return c;
      const sorted = [...(c.types || [])].sort((a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug));
      const idx = sorted.findIndex((t) => t.slug === typeSlug);
      const j = direction === 'up' ? idx - 1 : idx + 1;
      if (idx < 0 || j < 0 || j >= sorted.length) return c;
      const copy = [...sorted];
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return { ...c, types: copy.map((t, i) => ({ ...t, sort_order: i })) };
    });
    setEditableCategories(categories);
    queueCatalogSave(0, { categories, products: editableProducts });
  };

  const deleteCategory = (slug) => {
    if (editableCategories.length <= 1) {
      setStatus('Keep at least one category.');
      return;
    }
    if (!window.confirm(`Delete category “${slug}” and all its products? This saves immediately.`)) return;
    const remainingCats = editableCategories
      .filter((c) => c.slug !== slug)
      .map((c, i) => ({
        ...c,
        sort_order: i,
        subcategories: c.subcategories || [],
        types: c.types || [],
      }));
    const products = { ...editableProducts };
    delete products[slug];
    setEditableCategories(remainingCats);
    setEditableProducts(products);
    setSelectedCategory((prev) => (prev !== slug ? prev : remainingCats[0]?.slug || ''));
    queueCatalogSave(0, { categories: remainingCats, products });
    setStatus('Category removed. Saving…');
  };

  const logoutAdmin = async () => {
    await authLogout();
    setStatus('Signed out.');
    navigate('/login', { replace: true });
  };

  const deleteProduct = (category, index) => {
    if (!window.confirm('Delete this product?')) return;
    const updated = [...(editableProducts[category] || [])];
    updated.splice(index, 1);
    const products = { ...editableProducts, [category]: updated };
    setEditableProducts(products);
    queueCatalogSave(0, { categories: editableCategories, products });
    setStatus('Product removed. Saving…');
  };

  const handleDownload = () => {
    const blob = new Blob([rawJson], { type: 'application/json;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'products.json';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setStatus('Downloaded products.json.');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rawJson);
      setStatus('JSON copied.');
    } catch {
      setStatus('Copy failed.');
    }
  };

  const handleValidate = () => {
    try {
      const parsed = JSON.parse(rawJson);
      if (!parsed.products || typeof parsed.products !== 'object') {
        setStatus('Invalid: expected products object.');
        return;
      }
      setStatus('JSON is valid.');
    } catch (validationError) {
      setStatus(`Invalid JSON: ${validationError.message}`);
    }
  };

  const ensureCategoryInEditor = useCallback(
    (meta) => {
      if (!meta?.slug) return;
      const key = normalizeCategorySlug(meta.slug);
      if (editableCategories.some((c) => normalizeCategorySlug(c.slug) === key)) return;
      setEditableCategories((prev) =>
        [...prev, meta].sort((a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug))
      );
    },
    [editableCategories]
  );

  const openAddProduct = (categorySlug = null) => {
    const explicit = normalizeCategorySlug(categorySlug);
    const preferred =
      explicit || normalizeCategorySlug(selectedCategory) || normalizeCategorySlug(sortedEditableCategories[0]?.slug);

    let meta = resolveCategoryMeta(editableCategories, editableProducts, preferred);

    if (!meta && explicit) {
      setStatus(
        `Category “${explicit}” was not found. Refresh the page, or add shop groups under Categories for that section.`
      );
      return;
    }

    if (!meta) {
      setStatus('Create a category first (Categories tab), then add products.');
      setAdminTab('taxonomy');
      return;
    }

    ensureCategoryInEditor(meta);
    const cat = normalizeCategorySlug(meta.slug);
    if (normalizeCategorySlug(selectedCategory) !== cat) {
      setSelectedCategory(cat);
    }
    setAdminTab('products');
    setProductModal({ open: true, mode: 'add', category: cat, index: null });
  };

  const openEditProduct = (category, index) => {
    const cat = normalizeCategorySlug(category);
    const meta = resolveCategoryMeta(editableCategories, editableProducts, cat);
    if (meta) ensureCategoryInEditor(meta);
    setProductModal({ open: true, mode: 'edit', category: cat || category, index });
  };

  const closeProductModal = () => {
    setProductModal({ open: false, mode: 'add', category: '', index: null });
  };

  const saveProductFromModal = (draft) => {
    const name = String(draft.name || '').trim();
    if (!name) {
      setStatus('Name is required.');
      return;
    }
    const price = Number(draft.price);
    const resolvedPrice = Number.isFinite(price) ? price : 0;
    let catalogSnapshot = null;

    if (productModal.mode === 'add') {
      const cat = normalizeCategorySlug(productModal.category);
      const allIds = Object.values(editableProducts).flatMap((arr) =>
        Array.isArray(arr) ? arr.map((p) => p.id) : []
      );
      const sub = String(draft.subcategory || '').trim() || 'all';
      const previewId = String(draft.id || '').trim();
      const taken = new Set(allIds.map((x) => String(x || '').trim().toLowerCase()).filter(Boolean));
      const id =
        previewId && !taken.has(previewId.toLowerCase())
          ? previewId
          : generateMeaningfulProductId(cat, sub, allIds);
      const newProduct = { ...defaultProduct, ...draft, id, name, price: resolvedPrice };
      catalogSnapshot = {
        categories: editableCategories,
        products: {
          ...editableProducts,
          [cat]: [...(editableProducts[cat] || []), newProduct],
        },
      };
      setEditableProducts(catalogSnapshot.products);
      setStatus('Product added. Saving…');
    } else {
      const { index } = productModal;
      const category = modalProductCategoryKey;
      const list = [...(editableProducts[category] || [])];
      if (list[index] == null) {
        setStatus('Product not found.');
        return;
      }
      list[index] = {
        ...list[index],
        ...draft,
        id: list[index].id,
        name,
        price: resolvedPrice,
      };
      catalogSnapshot = {
        categories: editableCategories,
        products: { ...editableProducts, [category]: list },
      };
      setEditableProducts(catalogSnapshot.products);
      setStatus('Product updated. Saving…');
    }
    closeProductModal();
    queueCatalogSave(0, catalogSnapshot);
  };

  const modalCategoryMeta = useMemo(() => {
    if (!productModal.open) return null;
    const key = normalizeCategorySlug(productModal.category);
    return (
      resolveCategoryMeta(editableCategories, editableProducts, key) ||
      sortedEditableCategories.find((c) => normalizeCategorySlug(c.slug) === key) ||
      null
    );
  }, [
    productModal.open,
    productModal.category,
    editableCategories,
    editableProducts,
    sortedEditableCategories,
  ]);
  const modalProductCategoryKey = normalizeCategorySlug(productModal.category);
  const modalInitialProduct =
    productModal.mode === 'edit' && productModal.index != null
      ? editableProducts[modalProductCategoryKey]?.[productModal.index] || defaultProduct
      : defaultProduct;

  const uploadServerMediaForEdit = async (file, fieldName) => {
    const maxVideoBytes = 100 * 1024 * 1024;
    if (fieldName === 'video' && file.size > maxVideoBytes) {
      throw new Error(`Video is too large (${Math.round(file.size / (1024 * 1024))} MB). Maximum is ${maxVideoBytes / (1024 * 1024)} MB.`);
    }

    const formData = new FormData();
    formData.append(fieldName, file);
    const tok = String(sessionToken || '').trim();
    if (tok) formData.append('session_token', tok);
    const response = await fetch('/upload_image.php', {
      method: 'POST',
      headers: adminHeaders(),
      body: formData,
    });
    const text = await response.text();
    const plainSnippet = String(text || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 400);

    const postTooLarge =
      /POST Content-Length of \d+ bytes exceeds the limit|exceeds the limit of \d+ bytes|post_max_size|upload_max_filesize/i.test(
        text
      );

    let result = {};
    try {
      result = text ? JSON.parse(text) : {};
    } catch {
      if (postTooLarge) {
        throw new Error(
          'Upload was rejected because the file is larger than PHP allows (post_max_size / upload_max_filesize). ' +
            'Restart your PHP server with higher limits. For this project run: npm run php:serve ' +
            '(it sets 128M upload and 132M post). On shared hosting, raise those values in php.ini or the host control panel.'
        );
      }
      throw new Error(
        response.ok
          ? 'Upload response was not JSON (PHP may have printed a warning). Is PHP running and upload_image.php reachable?'
          : `Upload failed (HTTP ${response.status}). ${plainSnippet || 'Unknown error.'}`
      );
    }
    if (!response.ok || !result.success) {
      if (postTooLarge && (!result.error || String(result.error).trim() === '')) {
        throw new Error(
          'Upload was rejected because the file is larger than PHP allows (post_max_size / upload_max_filesize). ' +
            'Use npm run php:serve for local dev, or increase those settings on your server.'
        );
      }
      throw new Error(result.error || plainSnippet || 'Upload error');
    }
    return result.path;
  };

  const uploadServerImageForEdit = (file) => uploadServerMediaForEdit(file, 'image');
  const uploadServerVideoForEdit = (file) => uploadServerMediaForEdit(file, 'video');

  const adminGateOk =
    authReady && Boolean(String(sessionToken || '').trim()) && userRole === 'admin';
  const adminUnlocked = adminGateOk;

  if (!authReady) {
    return (
      <div className="admin-page">
        <Helmet>
          <title>Admin | Saloni Designs</title>
        </Helmet>
        <div className="container py-5">
          <div className="d-flex align-items-center gap-3 text-muted">
            <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            <p className="small mb-0">Checking your session…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!adminGateOk) {
    return (
      <div className="admin-page">
        <Helmet>
          <title>Admin | Saloni Designs</title>
        </Helmet>
        <div className="container py-5">
          <div className="d-flex align-items-center gap-3 text-muted">
            <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            <p className="small mb-0">Taking you to the right place…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`admin-page${adminTab === 'site' || adminTab === 'shows' || adminTab === 'orders' || adminTab === 'bespoke' || adminTab === 'coupons' || adminTab === 'reviews' || adminTab === 'analytics' ? ' admin-page--direct-tab' : ''}`}
    >
      <Helmet>
        <title>Admin | Saloni Designs</title>
      </Helmet>
      <div className="container admin-page__container">
        <div className="admin-shell">
          <div className="admin-shell__sticky">
            <div className="admin-shell-card">
              <AdminCatalogTop
                catalogDbActive={catalogDbActive}
                adminUsername={adminUsername}
                onLogout={logoutAdmin}
                onPublishCatalog={publishCatalog}
                onSaveSiteSettings={() =>
                  document.getElementById('admin-site-settings-form')?.requestSubmit()
                }
                adminTab={adminTab}
                saving={saving}
                dirty={dirty}
                dataSource={dataSource}
                loading={loading}
              />
              <AdminNav active={adminTab} onChange={setAdminTab} dirty={dirty || saving} />
            </div>
          </div>

          <div className="admin-shell__body">
          {adminTab !== 'shows' && adminTab !== 'site' && adminTab !== 'orders' && adminTab !== 'coupons' && adminTab !== 'reviews' && adminTab !== 'analytics' && loading && (
            <div className="admin-panel-alert admin-panel-alert--info mb-3">Loading catalog…</div>
          )}
          {adminTab !== 'shows' && adminTab !== 'site' && adminTab !== 'orders' && adminTab !== 'coupons' && adminTab !== 'reviews' && adminTab !== 'analytics' && error && (
            <div className="admin-panel-alert admin-panel-alert--warning mb-3">{error}</div>
          )}

          {(adminTab === 'shows' || adminTab === 'site' || adminTab === 'orders' || adminTab === 'bespoke' || adminTab === 'coupons' || adminTab === 'reviews' || adminTab === 'analytics' || !loading) && (
            <>

              {adminTab === 'shows' && <AdminShowsPanel sessionToken={sessionToken} setStatus={setStatus} />}

              {adminTab === 'site' && (
                <AdminSitePanel
                  sessionToken={sessionToken}
                  setStatus={setStatus}
                  catalogCategories={sortedEditableCategories}
                  catalogProducts={editableProducts}
                />
              )}

              {adminTab === 'orders' && (
                <AdminOrdersPanel sessionToken={sessionToken} setStatus={setStatus} />
              )}

              {adminTab === 'bespoke' && (
                <AdminBespokeRequestsPanel sessionToken={sessionToken} setStatus={setStatus} />
              )}

              {adminTab === 'coupons' && (
                <AdminCouponsPanel sessionToken={sessionToken} setStatus={setStatus} />
              )}

              {adminTab === 'reviews' && (
                <AdminReviewsPanel sessionToken={sessionToken} setStatus={setStatus} />
              )}

              {adminTab === 'analytics' && (
                <AdminAnalyticsPanel
                  sessionToken={sessionToken}
                  onGoOrders={() => setAdminTab('orders')}
                  onGoReviews={() => setAdminTab('reviews')}
                />
              )}

              {adminTab === 'overview' && !loading && (
                <AdminOverviewPanel
                  sortedCategories={sortedEditableCategories}
                  editableProducts={editableProducts}
                  catalogDbActive={catalogDbActive}
                  onGoAnalytics={() => setAdminTab('analytics')}
                  onGoProducts={() => setAdminTab('products')}
                />
              )}

              {adminTab === 'taxonomy' && !loading && (
                <AdminTaxonomyPanel
                  sortedEditableCategories={sortedEditableCategories}
                  editableCategories={editableCategories}
                  editableProducts={editableProducts}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  newCatName={newCatName}
                  setNewCatName={setNewCatName}
                  newCatHeroTagline={newCatHeroTagline}
                  setNewCatHeroTagline={setNewCatHeroTagline}
                  newCatHeroBlurb={newCatHeroBlurb}
                  setNewCatHeroBlurb={setNewCatHeroBlurb}
                  newSubName={newSubName}
                  setNewSubName={setNewSubName}
                  setNewSubParent={setNewSubParent}
                  addCategory={addCategory}
                  addSubcategory={addSubcategory}
                  deleteSubcategory={deleteSubcategory}
                  reorderSubcategories={reorderSubcategories}
                  updateCategoryName={updateCategoryName}
                  updateCategoryHero={updateCategoryHero}
                  reorderCategories={reorderCategories}
                  deleteCategory={deleteCategory}
                  updateCategoryVisible={updateCategoryVisible}
                  updateSubcategoryName={updateSubcategoryName}
                  updateSubcategoryVisible={updateSubcategoryVisible}
                  updateCategoryReturnAllowance={updateCategoryReturnAllowance}
                  updateSubcategoryReturnAllowance={updateSubcategoryReturnAllowance}
                  updateTypeReturnAllowance={updateTypeReturnAllowance}
                  onAddProduct={(slug) => openAddProduct(slug)}
                  onGoProducts={() => setAdminTab('products')}
                  newTypeSlug={newTypeSlug}
                  setNewTypeSlug={setNewTypeSlug}
                  newTypeName={newTypeName}
                  setNewTypeName={setNewTypeName}
                  addProductType={addProductType}
                />
              )}

              {adminTab === 'products' && !loading && (
                <AdminProductsPanel
                  sortedEditableCategories={sortedEditableCategories}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  editableProducts={editableProducts}
                  productSearch={productSearch}
                  setProductSearch={setProductSearch}
                  stockFilter={productStockFilter}
                  setStockFilter={setProductStockFilter}
                  onAddProduct={openAddProduct}
                  onEditProduct={openEditProduct}
                  onDeleteProduct={deleteProduct}
                />
              )}

              {adminTab === 'jewelry' && (
                <AdminJewelryProfilesPanel sessionToken={sessionToken} setStatus={setStatus} />
              )}

              {adminTab === 'advanced' && !loading && (
                <AdminAdvancedPanel
                  rawJson={rawJson}
                  onValidate={handleValidate}
                  onCopy={handleCopy}
                  onDownload={handleDownload}
                />
              )}
            </>
          )}
          </div>
        </div>
      </div>

      <ProductEditorModal
        key={`${productModal.mode}-${productModal.category}-${productModal.index ?? 'new'}`}
        show={adminUnlocked && productModal.open}
        mode={productModal.mode}
        categoryMeta={modalCategoryMeta}
        initialProduct={modalInitialProduct}
        existingProductIds={Object.values(editableProducts).flatMap((arr) =>
          Array.isArray(arr) ? arr.map((p) => p.id) : []
        )}
        canUploadToServer={Boolean(String(sessionToken || '').trim())}
        uploadServerImage={uploadServerImageForEdit}
        uploadServerVideo={uploadServerVideoForEdit}
        onClose={closeProductModal}
        onSave={saveProductFromModal}
        setStatus={setStatus}
      />

      {adminUnlocked ? (
        <AdminShellFooter
          status={status}
          dirty={dirty}
          saving={saving}
          catalogDbActive={catalogDbActive}
        />
      ) : null}
    </div>
  );
};

export default Admin;
